import { useState } from 'react'
import { Eye, Pencil, Plus } from 'lucide-react'
import { EmptyState, ErrorState, FormDialog, PageHeader, StatusBadge } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useEmployeeOptions } from '@/features/employee'
import { toApiProblem } from '@/lib/api/client'
import { useLetterMutations, useLetterPreview, useLetterTemplates } from '../api'
import { Select } from '@/components/ui/select'

interface Draft {
  id?: string
  code: string
  name: string
  description: string
  subject: string
  bodyHtml: string
  pageSize: string
  marginMm: string
  active: boolean
}

const EMPTY: Draft = {
  code: '',
  name: '',
  description: '',
  subject: '',
  bodyHtml: '<html><body>\n  <p>Date: <span th:text="${today}">today</span></p>\n  <p th:text="${employee.displayName}">name</p>\n</body></html>',
  pageSize: 'A4',
  marginMm: '20',
  active: true,
}

/**
 * Writing the letters this company issues.
 *
 * <p>Behind `letter:configure`, deliberately narrower than issuing. A Thymeleaf template is
 * code — it evaluates expressions — so whoever can edit one can change what every letter says.
 *
 * <p>Preview renders against a real employee and shows the HTML exactly as the PDF renderer
 * will receive it. A template that looks right in an editor and wrong on paper is the usual
 * outcome of previewing something else.
 */
export function LetterTemplatesPage() {
  useBreadcrumb()
  const templates = useLetterTemplates()
  const employees = useEmployeeOptions()
  const { createTemplate, updateTemplate } = useLetterMutations()
  const preview = useLetterPreview()

  const [draft, setDraft] = useState<Draft | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [previewHtml, setPreviewHtml] = useState<string | null>(null)
  const [previewFor, setPreviewFor] = useState<string | null>(null)

  async function save() {
    if (!draft) return
    setError(null)
    const body = {
      code: draft.code,
      name: draft.name,
      description: draft.description || null,
      subject: draft.subject || null,
      bodyHtml: draft.bodyHtml,
      pageSize: draft.pageSize,
      marginMm: Number(draft.marginMm || 20),
      active: draft.active,
    }
    try {
      if (draft.id) {
        await updateTemplate.mutateAsync({ id: draft.id, ...body })
      } else {
        await createTemplate.mutateAsync(body)
      }
      setDraft(null)
    } catch (caught) {
      setError(toApiProblem(caught)?.detail ?? 'That template could not be saved')
    }
  }

  async function runPreview(templateId: string) {
    const employeeId = employees.data?.[0]?.id
    if (!employeeId) return
    setPreviewFor(templateId)
    setError(null)
    try {
      setPreviewHtml(await preview.mutateAsync({ id: templateId, employeeId }))
    } catch (caught) {
      setPreviewHtml(null)
      setError(toApiProblem(caught)?.detail ?? 'That template could not be rendered')
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Letter templates"
        subtitle="What the company's letters say. Editing one never changes a letter already issued."
        actions={
          <Button type="button" onClick={() => { setError(null); setDraft({ ...EMPTY }) }}>
            <Plus className="size-4" aria-hidden="true" />
            Write a template
          </Button>
        }
      />

      <Card>
        {templates.isPending && <Skeleton className="m-4 h-32" />}
        {templates.isError && (
          <ErrorState error={templates.error} onRetry={() => void templates.refetch()} />
        )}
        {templates.isSuccess && templates.data.length === 0 && (
          <EmptyState
            title="No templates yet"
            description="Write one, then point a request type at it to issue it on approval."
          />
        )}
        {(templates.data?.length ?? 0) > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full min-w-[44rem] text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
                  <th scope="col" className="p-3 font-medium">Name</th>
                  <th scope="col" className="p-3 font-medium">Code</th>
                  <th scope="col" className="p-3 font-medium">Page</th>
                  <th scope="col" className="p-3 font-medium">Status</th>
                  <th scope="col" className="p-3"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {templates.data?.map((template) => (
                  <tr key={template.id} className="border-b border-[var(--color-border)] last:border-0">
                    <td className="p-3 font-medium text-[var(--color-heading)]">
                      {template.name}
                      {template.seeded && (
                        <span className="ms-2 text-xs font-normal text-[var(--color-muted)]">seeded</span>
                      )}
                    </td>
                    <td className="p-3"><code className="text-xs">{template.code}</code></td>
                    <td className="p-3 text-[var(--color-muted)]">
                      {template.pageSize}, {template.marginMm}mm
                    </td>
                    <td className="p-3">
                      <StatusBadge status={template.active ? 'ACTIVE' : 'INACTIVE'} />
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          type="button" size="sm" variant="ghost"
                          disabled={preview.isPending || (employees.data?.length ?? 0) === 0}
                          onClick={() => void runPreview(template.id)}
                        >
                          <Eye className="size-3.5" aria-hidden="true" />
                          Preview
                        </Button>
                        <Button
                          type="button" size="icon" variant="ghost"
                          aria-label={`Edit ${template.name}`}
                          onClick={() => {
                            setError(null)
                            setDraft({
                              id: template.id,
                              code: template.code,
                              name: template.name,
                              description: template.description ?? '',
                              subject: template.subject ?? '',
                              bodyHtml: template.bodyHtml,
                              pageSize: template.pageSize,
                              marginMm: String(template.marginMm),
                              active: template.active,
                            })
                          }}
                        >
                          <Pencil className="size-4" aria-hidden="true" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {error && !draft && <p role="alert" className="text-sm text-[var(--color-danger)]">{error}</p>}

      {previewHtml != null && (
        <Card className="p-4">
          <h2 className="mb-2 text-sm font-semibold text-[var(--color-heading)]">
            Preview — {templates.data?.find((t) => t.id === previewFor)?.name}
          </h2>
          <p className="mb-2 text-xs text-[var(--color-muted)]">
            Rendered against a real employee. This is exactly what the PDF renderer receives.
          </p>
          {/* Sandboxed: a template is administrator-authored, but rendering it into this
              document would let it reach the app's own DOM and cookies. */}
          <iframe
            title="Letter preview"
            sandbox=""
            srcDoc={previewHtml}
            className="h-[28rem] w-full rounded-[var(--radius-card)] border border-[var(--color-border)] bg-white"
          />
        </Card>
      )}

      <FormDialog
        open={draft != null}
        onOpenChange={(next) => !next && setDraft(null)}
        title={draft?.id ? `Edit ${draft.name}` : 'Write a letter template'}
        description="Thymeleaf. The model gives you employee, company, letter and today."
        loading={createTemplate.isPending || updateTemplate.isPending}
        error={error}
        onSubmit={() => void save()}
        size="lg"
      >
        {draft && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="lt-name">Name</Label>
              <Input id="lt-name" required value={draft.name}
                     onChange={(event) => setDraft({ ...draft, name: event.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="lt-code">Code</Label>
              <Input id="lt-code" required value={draft.code} disabled={draft.id != null}
                     onChange={(event) => setDraft({ ...draft, code: event.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="lt-page">Page size</Label>
              <Select id="lt-page" value={draft.pageSize}
                      onChange={(event) => setDraft({ ...draft, pageSize: event.target.value })}
                      className="w-full">
                {['A4', 'LETTER', 'LEGAL'].map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="lt-margin">Margin (mm)</Label>
              <Input id="lt-margin" type="number" min={0} max={60} value={draft.marginMm}
                     onChange={(event) => setDraft({ ...draft, marginMm: event.target.value })} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="lt-subject">Subject line</Label>
              <Input id="lt-subject" value={draft.subject}
                     onChange={(event) => setDraft({ ...draft, subject: event.target.value })} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="lt-body">Body</Label>
              <textarea
                id="lt-body" required rows={12} value={draft.bodyHtml}
                onChange={(event) => setDraft({ ...draft, bodyHtml: event.target.value })}
                className="w-full rounded-[var(--radius-input)] border border-[var(--color-border)] bg-[var(--color-card)] p-2 font-mono text-xs"
              />
              <p className="text-xs text-[var(--color-muted)]">
                Available: employee.displayName, employee.employeeCode, employee.joinDate,
                employee.designation, employee.nationality, company.name, company.address,
                letter.reference, today.
              </p>
            </div>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
